import * as d3 from 'd3';
import { radiansToDegrees } from '@turf/turf';
import { isUndefined, last, map, range } from 'lodash';

import { createRadialChartTemplate } from '@/charts/common';
import { GenericObject, getYValue } from '@/charts/helpers';

const MIN_CHART_WIDTH = 40;
const LEGEND_AREA_HEIGHT = 100;
const LEGEND_HEIGHT = 10;
const GRID_STROKE_WIDTH = 1;
const CURSOR_STROKE_WIDTH = 2;
const OUTER_RING_STROKE_WIDTH = 2;

const angleStep = 2.011173184357542; // degrees. 2 deg resolution
const gridRingStep = 0.1;

const DEFAULT_MAX_FREQUENCY = 0.4;
const DEFAULT_MAX_ENERGY = 1;

const HALF_PI = Math.PI / 2;

export const radialSpectrumChart = <Datum extends GenericObject>({
    dataset,
    containerId,
    config,
}: {
    dataset: Datum[];
    containerId: string;
    config: {
        radiusKey: string;
        angleKey: string;
        colorKey: string;
        legendLabel: string;
        legendUnits: string;
        maxFrequency?: number | null;
        maxEnergy?: number | null;
        radiusBinWidth: number;
        ignoreValuesBelow: number;
        width: number;
        showHoverCursors?: boolean;
    };
}) => {
    if (!dataset) {
        return;
    }

    const chartWidth = Math.max(config.width, MIN_CHART_WIDTH);
    const chartHeight = chartWidth + LEGEND_AREA_HEIGHT;
    // at sm we hide a bunch of labels to maximize chart space
    const layoutSize = chartWidth > 200 ? 'md' : 'sm';
    const { radiusBinWidth } = config;

    const { svg, grid, width, height, radius, appendGridRingsAndSpokes } = createRadialChartTemplate({
        containerId,
        chartHeight,
        chartWidth,
        compassLabels: layoutSize !== 'sm',
    });

    if (isUndefined(svg) || isUndefined(grid) || isUndefined(width) || isUndefined(height)) {
        return;
    }

    const legendWidth = 0.8 * chartWidth;
    const highEnergyDataset = dataset.filter((d) => getYValue(d, config.colorKey) >= config.ignoreValuesBelow);
    const angleScale = d3
        .scaleLinear()
        // Domain (from estimatedWaveSpectrum) is degrees clockwise from north
        .domain([0, 360])
        // Range (via d3.arc().startAngle()) is specified in radians, with 0 at -y (12 o’clock) and positive angles proceeding clockwise
        .range([0, 2 * Math.PI]);

    const [freqDataMin = 0, freqDataMax = 0] = d3.extent(highEnergyDataset, (d) => getYValue(d, config.radiusKey));
    const freqMin = Math.min(0, freqDataMin);
    const freqMax = Math.max(config.maxFrequency ?? DEFAULT_MAX_FREQUENCY, freqDataMax);

    const radiusScale = d3.scaleLinear().domain([freqMin, freqMax]).range([0, radius]);
    const colorDataMax = d3.max(highEnergyDataset, (d) => getYValue(d, config.colorKey)) ?? 0;
    const colorMax = Math.max(config.maxEnergy ?? DEFAULT_MAX_ENERGY, colorDataMax);

    const color = d3.scaleSequential(d3.interpolatePlasma).domain([colorMax, 0]);

    const gridRingRadii = range(Math.trunc(freqMin / 10) * 10, freqMax, gridRingStep).map((freq) => ({
        freq,
        px: radiusScale(freq),
    }));

    // one darker grid behind the data
    appendGridRingsAndSpokes(map(gridRingRadii, 'px'));

    const arcGen = d3
        .arc<Datum>()
        .innerRadius((d) => radiusScale(getYValue(d, config.radiusKey) - config.radiusBinWidth))
        .outerRadius((d) => radiusScale(getYValue(d, config.radiusKey)))
        .startAngle((d) => angleScale(getYValue(d, config.angleKey)))
        .endAngle((d) => angleScale(getYValue(d, config.angleKey) + angleStep));

    grid.selectAll('path')
        .data(highEnergyDataset)
        .join('path')
        .attr('d', arcGen)
        .attr('fill', (d) => color(getYValue(d, config.colorKey)))
        .attr('stroke', 'none');

    // another much lighter grid in front of the data
    appendGridRingsAndSpokes(map(gridRingRadii, 'px'), { opacity: 0.2 });

    /* Hover cursor on angle */
    const angleCursorGroup = grid.append('g').attr('visibility', 'hidden');
    const angleCursorLabelContainer = angleCursorGroup.append('g').attr('transform', `translate(0, ${-radius - 12})`);
    const angleCursorLabel = angleCursorLabelContainer
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('y', 5)
        .style('font-size', '12px');
    angleCursorGroup
        .append('line')
        .attr('x1', 0)
        .attr('y1', -radius - 5)
        .attr('x2', 0)
        .attr('y2', -radius + 5)
        .attr('stroke', 'black')
        .attr('stroke-width', CURSOR_STROKE_WIDTH);

    /*
        Frequency scale bar
    */
    const scaleGroupOffset = (grid.node()?.getBoundingClientRect().height || 380) + 28;
    const scaleGroup = svg.append('g').attr('transform', `translate(${width / 2}, ${scaleGroupOffset})`);
    const radialScaleLabel = layoutSize === 'sm' ? 'Hz' : 'Frequency (Hz)';
    scaleGroup
        .append('text')
        .attr('x', -12)
        .attr('y', 5)
        .attr('text-anchor', 'end')
        .style('font-size', '12px')
        .style('fill', 'grey')
        .text(radialScaleLabel);
    // main scale line
    scaleGroup
        .append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', radius)
        .attr('y2', 0)
        .attr('stroke', 'grey')
        .attr('stroke-width', GRID_STROKE_WIDTH);

    // ticks and labels
    // try to make sure each tick label gets at least 16 px
    const tickLabelDensity = Math.ceil(16 / radiusScale(gridRingStep));
    gridRingRadii.forEach(({ freq, px }, idx) => {
        if (idx === 0) {
            // First tick is a circle with no label (0 Hz isn't a wave)
            scaleGroup
                .append('circle')
                .attr('x1', 0)
                .attr('y1', -5)
                .attr('x2', 0)
                .attr('y2', 5)
                .attr('fill', 'grey')
                .attr('r', 3);
        } else {
            if (layoutSize !== 'sm' && idx % tickLabelDensity == 0) {
                scaleGroup
                    .append('text')
                    .attr('x', px)
                    .attr('y', 16)
                    .style('text-anchor', 'middle')
                    .style('font-size', '10px')
                    .style('fill', 'grey')
                    .text(freq.toFixed(1));
            }
            scaleGroup
                .append('line')
                .attr('x1', px)
                .attr('y1', -5)
                .attr('x2', px)
                .attr('y2', 5)
                .attr('stroke', 'grey')
                .attr('stroke-width', GRID_STROKE_WIDTH);
        }
    });
    if (layoutSize === 'sm' || freqMax < gridRingStep || freqMax - last(gridRingRadii)!.freq > gridRingStep * 0.8) {
        scaleGroup
            .append('text')
            .attr('x', radius + 6)
            .attr('y', 16)
            .style('text-anchor', layoutSize === 'sm' ? 'end' : 'middle')
            .style('font-size', '10px')
            .style('fill', 'grey')
            .text(freqMax.toFixed(2));
    }
    scaleGroup
        .append('line')
        .attr('x1', radius)
        .attr('y1', -5)
        .attr('x2', radius)
        .attr('y2', 5)
        .attr('stroke', 'grey')
        .attr('stroke-width', OUTER_RING_STROKE_WIDTH);

    /* Hover cursor on radius legend */
    const radiusCursorGroup = scaleGroup.append('g').attr('visibility', 'hidden');
    const radiusCursorLabel = radiusCursorGroup
        .append('text')
        .attr('x', 0)
        .attr('y', -10)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px');
    radiusCursorGroup
        .append('line')
        .attr('x1', 0)
        .attr('y1', -7)
        .attr('x2', 0)
        .attr('y2', 7)
        .attr('stroke', 'black')
        .attr('stroke-width', CURSOR_STROKE_WIDTH);

    /*
        Legend Bar
    */
    const legendOffset = scaleGroupOffset + 10;
    const legendGroup = svg.append('g').attr('transform', `translate(${(width - legendWidth) / 2}, ${legendOffset})`);
    const gradientId = `${containerId}-legend-gradient`;
    const defs = legendGroup.append('defs');

    const gradient = defs
        .append('linearGradient')
        .attr('id', gradientId)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '0%');

    d3.range(0, 1.01, 0.01).forEach((t) => {
        gradient
            .append('stop')
            .attr('offset', `${t * 100}%`)
            .attr('stop-color', d3.interpolatePlasma(1 - t));
    });

    const colorLegendLabel = layoutSize === 'sm' ? config.legendUnits : `${config.legendLabel} (${config.legendUnits})`;
    legendGroup
        .append('text')
        .attr('x', legendWidth / 2)
        .attr('y', 30)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', 'grey')
        .text(colorLegendLabel);

    legendGroup
        .append('rect')
        .attr('x', 0)
        .attr('y', 40)
        .attr('width', legendWidth)
        .attr('height', LEGEND_HEIGHT)
        .style('fill', `url(#${gradientId})`);

    const colorLegendLowerBoundLabel = legendGroup
        .append('text')
        .attr('x', 0)
        .attr('y', 65)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('fill', 'grey')
        .text(0);

    const colorLegendUpperBoundLabel = legendGroup
        .append('text')
        .attr('x', legendWidth)
        .attr('y', 65)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('fill', 'grey')
        .text(colorMax.toFixed(0));

    /* Hover cursor on color value legend */
    const valueCursorGroup = legendGroup.append('g').attr('visibility', 'hidden');
    const valueCursorLabel = valueCursorGroup
        .append('text')
        .attr('x', 0)
        .attr('y', 65)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px');
    valueCursorGroup
        .append('line')
        .attr('x1', 0)
        .attr('y1', 38)
        .attr('x2', 0)
        .attr('y2', 53)
        .attr('stroke', 'black')
        .attr('stroke-width', CURSOR_STROKE_WIDTH);

    svg.on('mousemove', (e) => {
        if (!config.showHoverCursors) {
            hideCursors();
            return;
        }

        const [xCoord, yCoord] = d3.pointer(e, e.target);
        const r = Math.sqrt(xCoord * xCoord + yCoord * yCoord);
        if (r <= radius) {
            const cursorRadius = Math.ceil(radiusScale.invert(r) / radiusBinWidth) * radiusBinWidth;
            const compassAngleRad = Math.atan2(yCoord, xCoord) + HALF_PI;
            const compassAngleDeg = (radiansToDegrees(compassAngleRad) + 360) % 360;
            const dataAngle = (angleScale.invert(compassAngleRad) + 360) % 360;
            const datum = dataset.find(
                (d) =>
                    Math.abs((d[config.radiusKey] as number) - cursorRadius) <= radiusBinWidth / 2 &&
                    Math.abs((d[config.angleKey] as number) - dataAngle) <= angleStep / 2
            );
            if (!datum) {
                hideCursors();
                return;
            }

            const direction = datum[config.angleKey] as number;
            const freq = datum[config.radiusKey] as number;
            const colorValue = datum[config.colorKey] as number;

            radiusCursorLabel.text(freq.toFixed(3));
            radiusCursorGroup.attr('transform', `translate(${radius * ((freq + freqMin) / (freqMax - freqMin))},0)`);
            radiusCursorGroup.style('visibility', 'visible');

            let angleLabelXOffset = 0;
            if (compassAngleDeg >= 40 && compassAngleDeg <= 140) {
                angleLabelXOffset = 5;
            } else if (compassAngleDeg >= 220 && compassAngleDeg <= 320) {
                angleLabelXOffset = -5;
            }
            angleCursorLabel
                .text(direction.toFixed(0))
                .attr('x', angleLabelXOffset)
                // counter-rotate the label to keep text right-side up
                .attr('transform', `rotate(-${direction} 0 0)`);
            angleCursorGroup.style('visibility', 'visible');
            angleCursorGroup.attr('transform', `rotate(${direction})`);

            const valueCursorX = legendWidth * (colorValue / colorMax);
            let valueLowerBoundVisibility = 'visible';
            let valueUpperBoundVisibility = 'visible';
            if (colorValue >= config.ignoreValuesBelow) {
                valueCursorLabel.style('visibility', 'visible');
                // if the value cursor label is within 20px of the lower bound
                // or 24px of the upper bound (since the label is wider),
                // hide the bound label to avoid overlap
                if (valueCursorX < 20) {
                    valueLowerBoundVisibility = 'hidden';
                } else if (valueCursorX > legendWidth - 24) {
                    valueUpperBoundVisibility = 'hidden';
                }
            } else {
                valueCursorLabel.style('visibility', 'hidden');
            }
            colorLegendLowerBoundLabel.style('visibility', valueLowerBoundVisibility);
            colorLegendUpperBoundLabel.style('visibility', valueUpperBoundVisibility);
            valueCursorLabel.text(colorValue.toFixed(1));
            valueCursorGroup.attr('transform', `translate(${valueCursorX},0)`);
            valueCursorGroup.style('visibility', 'visible');
        } else {
            hideCursors();
        }
    });
    svg.on('mouseleave', () => {
        hideCursors();
    });

    function hideCursors() {
        radiusCursorGroup.style('visibility', 'hidden');
        angleCursorGroup.style('visibility', 'hidden');
        valueCursorGroup.style('visibility', 'hidden');
        valueCursorLabel.style('visibility', 'hidden');
        colorLegendLowerBoundLabel.style('visibility', 'visible');
        colorLegendUpperBoundLabel.style('visibility', 'visible');
    }
};
